
import {
  computed,
  defineComponent,
  onMounted,
  provide,
  reactive,
  ref,
} from 'vue'
import ApiService from '@/core/services/ApiService'
import { ElMessage, ElMessageBox } from 'element-plus'
import InterfaceFilterCompaniesData from '@/assets/ts/_utils/models/FilterCompaniesData'
import VueEasyLightbox from 'vue-easy-lightbox'
import isEmptyObject from '@/core/helpers/isEmptyObject'
import CompaniesFilter from '@/core/components/filters/CompaniesFilter.vue'

interface InterfaceCompaniesObject {
  id: number
  name: string
  checked: boolean
  emails: Array<string>
  phones: Array<string>
  status: string
  has_user: boolean
  reasons: Array<string>
  rejected_count: number
  created_at: string
  updated_at: string
  lightboxVisible: boolean
}

export default defineComponent({
  name: 'CompaniesList',
  components: { CompaniesFilter, VueEasyLightbox },
  setup() {
    const rowClasses =
      'd-flex flex-column align-items-start justify-content-start'
    const reasons = ref([])
    const checkedModel = ref(false)
    const isLoading = ref(false)
    const itemsList = ref<InterfaceCompaniesObject[]>([])
    const totalPages = ref(0)
    const totalItems = ref(0)
    const lightboxVisible = ref(false)
    const lightboxImage = ref<string | null>(null)

    const openLightbox = (src: string) => {
      lightboxImage.value = src
      lightboxVisible.value = true
    }

    const closeLightbox = () => {
      lightboxVisible.value = false
      lightboxImage.value = null
    }

    const companiesFilterData = reactive({
      page: null,
      limit: null,
      'filter[user_id]': null,
      'filter[name]': null,
      'filter[status]': null,
      'filter[email]': null,
      'filter[phone]': null,
    } as InterfaceFilterCompaniesData)

    provide('companiesFilterData', companiesFilterData)

    const convertDataForEdit = (
      items: InterfaceCompaniesObject[]
    ): InterfaceCompaniesObject[] => {
      return items.map(item => ({
        ...item,
        reasons: [],
        checked: false,
        lightboxVisible: false,
      }))
    }

    const requestItems = () => {
      const params: InterfaceFilterCompaniesData = { ...companiesFilterData }

      checkedModel.value = false
      isLoading.value = true
      itemsList.value = []

      ApiService.query('/admin/companies', { params })
        .then(response => {
          const data = response.data
          companiesFilterData.page = data.page
          companiesFilterData.limit = data.limit
          totalPages.value = data.pages
          totalItems.value = data.total
          itemsList.value = convertDataForEdit(data.items)
          isLoading.value = false
        })
        .catch(() => {
          ElMessage.warning('Ошибка запроса списка компаний')
          isLoading.value = false
        })
    }

    const requestReasons = async () => {
      const reasonsObject = await ApiService.query(
        '/admin/reference/moder-reasons?type=company',
        {}
      )
        .then(response => {
          return response.data
        })
        .catch(() => {
          console.error('Error to get reasons')
        })

      if (reasonsObject && !isEmptyObject(reasonsObject)) {
        for (const [key, value] of Object.entries(reasonsObject)) {
          reasons.value.push({
            key,
            value,
          })
        }
      } else {
        ElMessage.warning('Что-то не так с запросом причин')
      }
    }

    const detectUncheckedReasonsOnCheckedEntries = computed(() => {
      const entries = itemsList.value.map(item => {
        if (item.checked && item.reasons.length) {
          return 1
        } else if (item.checked && !item.reasons.length) {
          return 2
        } else if (!item.checked && item.reasons.length) {
          return 3
        }
      })
      return entries.some(item => item === 2)
    })

    const someCheckboxesAreChecked = computed(() => {
      return itemsList.value.some(item => item.checked === true)
    })

    const bulkRejectStatus = computed(() => {
      return (
        !someCheckboxesAreChecked.value ||
        detectUncheckedReasonsOnCheckedEntries.value
      )
    })

    const rejectButtonTitle = computed(() => {
      let title = ''
      if (
        !someCheckboxesAreChecked.value &&
        !detectUncheckedReasonsOnCheckedEntries.value
      ) {
        title = 'Выберите хотя бы одну компанию из списка'
      } else if (
        someCheckboxesAreChecked.value &&
        detectUncheckedReasonsOnCheckedEntries.value
      ) {
        title = 'Выберите причину отклонения для выбранных компаний'
      } else {
        title = 'Отклонить компании'
      }
      return title
    })

    const getReasonsOfCheckedEntries = computed(() => {
      const entriesObject = {}
      itemsList.value.forEach(item => {
        if (item.id && item.checked === true && item.reasons.length) {
          console.log('> item.reasons', item.reasons)
          entriesObject[item.id] = item.reasons.map(item => item['value']['id'])
        }
      })
      return entriesObject
    })

    const getCheckedEntriesIds = computed(() => {
      const idsArray = []
      itemsList.value.forEach(item => {
        if (item.id && item.checked === true) {
          idsArray.push(item.id)
        }
      })
      return idsArray
    })

    const requestToAllowBulkEntries = async ids => {
      const params = {
        ids: ids,
      }

      return await ApiService.post('/admin/companies/bulk-approve', params)
        .then(response => {
          ElMessage({
            type: 'success',
            message: 'Одобрение подтверждено',
          })
          return response.data.result
        })
        .catch(() => {
          ElMessage({
            type: 'warning',
            message: 'Что-то не так с массовым одобрением компаний',
          })
          return false
        })
    }

    const allowEntriesBulk = () => {
      ElMessageBox.confirm(
        'Вы действительно хотите одобрить выбранные компании?',
        'Внимание',
        {
          type: 'success',
          cancelButtonText: 'Отмена',
          confirmButtonText: 'Одобрить',
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              instance.confirmButtonLoading = true
              instance.confirmButtonText = 'В процессе...'
              const result = await requestToAllowBulkEntries(
                getCheckedEntriesIds.value
              )
              if (result) {
                instance.confirmButtonLoading = false
                requestItems()
                done()
              }
              done()
            } else {
              ElMessage({
                type: 'info',
                message: 'Одобрение отменено',
              })
              done()
            }
          },
        }
      )
    }

    const requestToRejectEntriesBulk = reasonsObject => {
      const params = {
        reasons: reasonsObject,
      }

      return ApiService.post('/admin/companies/bulk-reject', params)
        .then(response => {
          return response.data.result
        })
        .catch(() => {
          ElMessage({
            type: 'warning',
            message: 'Что-то не так с отклонением компаний',
          })
        })
    }

    const rejectEntriesBulk = () => {
      ElMessageBox.confirm(
        'Вы действительно хотите отклонить выбранные компании?',
        'Внимание',
        {
          type: 'warning',
          cancelButtonText: 'Отмена',
          confirmButtonText: 'Отклонить',
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              instance.confirmButtonLoading = true
              instance.confirmButtonText = 'В процессе...'
              const result = await requestToRejectEntriesBulk(
                getReasonsOfCheckedEntries.value
              )
              if (result) {
                instance.confirmButtonLoading = false
                requestItems()
                ElMessage({
                  type: 'success',
                  message: 'Отклонение компаний подтверждено',
                })
                done()
              }
            } else {
              ElMessage({
                type: 'info',
                message: 'Отклонение компаний отменено',
              })
              done()
            }
          },
        }
      )
    }

    const requestToDeleteBulkEntries = ids => {
      const params = {
        ids: ids,
      }

      return ApiService.post('/admin/companies/bulk-destroy', params)
        .then(response => {
          return response.data.result
        })
        .catch(() => {
          ElMessage({
            type: 'warning',
            message: 'Что-то не так с массовым удалением компаний',
          })
        })
    }

    const deleteEntriesBulk = () => {
      ElMessageBox.confirm(
        'Вы действительно хотите удалить выбранные компании?',
        'Внимание',
        {
          type: 'warning',
          cancelButtonText: 'Отмена',
          confirmButtonText: 'Удалить',
          beforeClose: (action, instance, done) => {
            if (action === 'confirm') {
              instance.confirmButtonLoading = true
              instance.confirmButtonText = 'В процессе...'
              const result = requestToDeleteBulkEntries(
                getCheckedEntriesIds.value
              )
              if (result) {
                instance.confirmButtonLoading = false
                requestItems()
                done()
              }
              ElMessage({
                type: 'success',
                message: 'Удаление компаний подтверждено',
              })
              done()
            } else {
              ElMessage({
                type: 'info',
                message: 'Удаление компаний отменено',
              })
              done()
            }
          },
        }
      )
    }

    const makeStatusTitle = status => {
      return status === 'confirmed'
        ? 'Одобренные'
        : status === 'rejected'
        ? 'Отклонённые'
        : status === 'not_verified'
        ? 'Не проверенные'
        : 'Nothing'
    }

    const checkItemOnReasonSelect = index => {
      const clickedItems = itemsList.value[index]
      clickedItems.checked = !!clickedItems.reasons.length
    }

    const syncParamsToUrl = params => {
      const newUrl =
        window.location.protocol +
        '//' +
        window.location.host +
        window.location.pathname +
        '?' +
        params
      // This replaces the current URL with the recreated.
      window.history.pushState({ path: newUrl }, '', newUrl)
    }

    const convertFilterToParams = (data: InterfaceFilterCompaniesData) => {
      let paramsAsString = ''
      // Iterate the filter object params.
      for (const [key, value] of Object.entries(data)) {
        if (value && Boolean(value)) {
          if (!paramsAsString.length) {
            // The 1st element in the params row.
            paramsAsString += key + '=' + value
          } else {
            // The rest elements in the params row.
            paramsAsString += '&' + key + '=' + value
          }
        }
      }
      // console.log('> convert >> paramsAsString', paramsAsString);
      return paramsAsString
    }

    const filterCompaniesButtonClick = () => {
      syncParamsToUrl(convertFilterToParams(companiesFilterData))
      requestItems()
    }

    const switchAllCheckboxes = () => {
      if (checkedModel.value) {
        itemsList.value.forEach(item => {
          item.checked = false
        })
      } else {
        itemsList.value.forEach(item => {
          item.checked = true
        })
      }
    }

    const tableHasContent = computed(() => {
      return itemsList.value.length > 0 && !isLoading.value
    })

    const requestToAllowEntry = item => {
      return ApiService.post(`/admin/companies/${item.id}/approve`, {
        id: item.id,
      })
        .then(response => {
          return response.data.result
        })
        .catch(err => {
          ElMessage({
            type: 'warning',
            message:
              err.response?.data?.message ??
              'Что-то не так с одобрением компании',
          })
        })
    }

    const allowEntry = item => {
      const entry = item
      ElMessageBox.confirm(
        `Вы действительно хотите одобрить эту компанию: <strong>${entry.name}</strong>?`,
        'Внимание',
        {
          type: 'success',
          cancelButtonText: 'Отмена',
          confirmButtonText: 'Одобрить',
          dangerouslyUseHTMLString: true,
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              const resultOfAllowEntry = await requestToAllowEntry(entry)
              instance.confirmButtonLoading = true
              instance.confirmButtonText = 'В процессе...'
              if (resultOfAllowEntry) {
                requestItems()
                instance.confirmButtonLoading = false
                ElMessage({
                  type: 'success',
                  message: 'Одобрение компании подтверждено',
                })
                done()
              }
            } else {
              ElMessage({
                type: 'warning',
                message: 'Одобрение компании отменено',
              })
              done()
            }
          },
        }
      )
    }

    const requestToRejectEntry = item => {
      const params = {
        reasons: item.reasons.map(item => item.value.id),
      }

      return ApiService.post(`/admin/companies/${item.id}/reject`, params)
        .then(response => {
          return response.data.result
        })
        .catch(err => {
          ElMessage({
            type: 'warning',
            message:
              err.response?.data?.message ??
              'Что-то не так с отклонением компании',
          })
        })
    }

    const rejectEntry = item => {
      const entry = item
      ElMessageBox.confirm(
        `Вы действительно хотите отклонить эту компанию: <strong>${entry.name}</strong>?`,
        'Внимание',
        {
          type: 'success',
          cancelButtonText: 'Отмена',
          confirmButtonText: 'Отклонить',
          dangerouslyUseHTMLString: true,
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              const resultOfAllowEntry = await requestToRejectEntry(entry)
              instance.confirmButtonLoading = true
              instance.confirmButtonText = 'В процессе...'
              if (resultOfAllowEntry) {
                requestItems()
                instance.confirmButtonLoading = false
                ElMessage({
                  type: 'success',
                  message: 'Отклонение компании подтверждено',
                })
                done()
              }
            } else {
              ElMessage({
                type: 'warning',
                message: 'Отклонение компании отменено',
              })
              done()
            }
          },
        }
      )
    }

    onMounted(() => {
      requestReasons()
    })

    return {
      rowClasses,
      isLoading,
      itemsList,
      companiesFilterData,
      totalPages,
      totalItems,
      reasons,
      makeStatusTitle,
      lightboxVisible,
      lightboxImage,
      openLightbox,
      closeLightbox,
      allowEntry,
      rejectEntry,
      allowEntriesBulk,
      rejectEntriesBulk,
      deleteEntriesBulk,
      bulkRejectStatus,
      rejectButtonTitle,
      checkedModel,
      switchAllCheckboxes,
      convertDataForEdit,
      checkItemOnReasonSelect,
      filterCompaniesButtonClick,
      tableHasContent,
      someCheckboxesAreChecked,
      isEmptyObject,
    }
  },
})
